/* global google */
import React, { useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { openAreaTray, openEntityTray } from '@actions/map-actions';
import {
  mapConfig,
  segmentDirections
} from '@constants/component-configs';
import { useShouldHighlight } from '@hooks/map-hooks';
import {
  getOneDirectionIcon,
  getBothDirectionIcon
} from '@icons/map-icons';
import { getHoverEntityId, getMapModesSet, getTrayEntitiesIds, getZoom } from '@selectors/map-selector';
import MarkerIcon from '@shared/marker-icon';
import Polygon from '@shared/polygon';
import Polyline from '@shared/polyline';
import { getEntityMode } from '@utils/entity-utils';
import { getEntityMarker, getEntityStarIcon, getMarkerAnchorStyle } from '@utils/icon-utils';
import { isMarkerVisible } from '@utils/markers-utils';

const EntityMapFigure = ({
  agencyTypeId,
  clustering,
  color: originalColor,
  highlighted,
  icon,
  id,
  markerSource,
  markerVisible: markerVisibleProp,
  name,
  openConflicts,
  opportunities,
  ownEntity,
  pendingConflicts,
  resolvedConflicts,
  segments,
  type
}) => {
  const dispatch = useDispatch();

  // Use the custom hook for highlight logic
  const { shouldHighlight, shouldHighlightSegment, selectedSegmentId } = useShouldHighlight(
    id,
    getTrayEntitiesIds,
    getHoverEntityId
  );

  // Redux state
  const zoom = useSelector(getZoom);
  const markers = useSelector(state => {
    if (markerSource && markerSource === 'group') {
      return state.groups.markers;
    }
    return state.markers;
  });
  const drawingMode = useSelector(state => state.map.drawing.mode);
  const mapModes = useSelector(getMapModesSet);

  const markerVisible = markerVisibleProp || isMarkerVisible(markers, zoom, type, id);
  const showOffset = ownEntity;
  const color = highlighted ? '#F83125' : originalColor;

  const getMarker = useCallback(highlight => {
    return getEntityMarker(
      agencyTypeId,
      type,
      icon,
      highlight,
      getEntityMode(openConflicts, pendingConflicts, resolvedConflicts, opportunities, mapModes)
    );
  }, [agencyTypeId, icon, mapModes, openConflicts, opportunities, pendingConflicts, resolvedConflicts, type]);

  const getOffsetMarker = useCallback(highlight => getEntityStarIcon(highlight), []);

  // Accessible name for the marker's gmp-advanced-marker element - Google Maps sets
  // role="button"/tabindex automatically on a clickable marker, but gives it no name of its
  // own, so screen reader users hear nothing when tabbing to it without this.
  const markerTitle = name || type;

  const getSegmentId = useCallback(
    segmentId => `${type}-${segmentId}`,
    [type]
  );

  const getPolylineId = useCallback(
    (segmentId, index) => `poly-${type}-${segmentId}-${index}`,
    [type]
  );

  const isHighlighted = useCallback(
    segmentId => shouldHighlight && shouldHighlightSegment(segmentId),
    [shouldHighlight, shouldHighlightSegment]
  );

  const getInnerStyle = useCallback((styleColor, isHighlightedStyle = false) => {
    if (isHighlightedStyle) {
      return { ...mapConfig.hoverStyle.inner, strokeColor: styleColor };
    }
    return { ...mapConfig.defaultStyle, strokeColor: styleColor };
  }, []);

  const getSegmentArrowOptions = useCallback((segment, innerOptions) => {
    const arrowIcon = { offset: '0', repeat: '20px' };
    let arrowOptions = {
      ...innerOptions,
      strokeOpacity: 0,
      zIndex: 205  // Arrows must be on top of all other Polylines.
    };
    if (segment.direction === segmentDirections.one.value) {
      arrowOptions = {
        ...arrowOptions,
        icons: [{
          ...arrowIcon,
          icon: getOneDirectionIcon()
        }]
      };
    }
    if (segment.direction === segmentDirections.both.value) {
      arrowOptions = {
        ...arrowOptions,
        icons: [{
          ...arrowIcon,
          icon: getBothDirectionIcon()
        }]
      };
    }
    return arrowOptions;
  }, []);

  const markerClick = useCallback(position => {
    if (drawingMode !== '') {
      // Don't allow clicking on a marker while we are drawing a shape.
      return;
    }
    if (clustering) {
      dispatch(openAreaTray(position));
    } else {
      const trayProps = {
        id,
        type,
        position
      };
      dispatch(openEntityTray(trayProps));
    }
  }, [clustering, dispatch, drawingMode, id, type]);

  // Pre-compute marker click handlers for each segment
  const markerClicks = useMemo(() => {
    const clicks = {};
    segments.forEach(segment => {
      clicks[segment.id] = () => markerClick(segment.center);
    });
    return clicks;
  }, [markerClick, segments]);

  const onMouseOver = useCallback(position => {
    // Some maps might not have the radius circle (like the group edit page).
    if (window.highlightCircle) {
      window.highlightCircle.setCenter(position);
    }
  }, []);

  // AdvancedMarker's mouse events carry a raw DOM MouseEvent (no latLng), so bind each
  // marker's own known position instead of reading it off the event.
  const markerMouseEnters = useMemo(() => {
    const handlers = {};
    segments.forEach(segment => {
      handlers[segment.id] = () => onMouseOver(segment.center);
    });
    return handlers;
  }, [onMouseOver, segments]);

  const isClickable = drawingMode === '';

  const renderSegment = useCallback((segment, highlight) => {
    let zIndex = highlight ? 202 : undefined;  // eslint-disable-line no-undefined
    if (highlighted) {
      zIndex = 220;
    }
    if (highlight) {
      const innerOptions = { ...getInnerStyle(color, true), zIndex };

      // When the segment is highlighted, we must show a "glow"
      // around it. Since we are restricted to only set color
      // and thickness for Google Maps Polylines, we achieve
      // that effect by rendering three Polylines at the same
      // place, with different colors:
      if (segment.path) {
        const lines = [
          <Polyline key={getPolylineId(segment.id, 0)} path={segment.path} options={innerOptions} />
        ];

        // Show the arrows only if the direction is set,
        // and if we are highlighting a specific segment.
        if (segment && segment.direction && selectedSegmentId === segment.id) {
          // Like in the edit page, add another Polyline which only contains the arrows:
          const arrowOptions = getSegmentArrowOptions(segment, innerOptions);
          lines.push(<Polyline key={getPolylineId(segment.id, 1)} path={segment.path} options={arrowOptions} />);
        }
        lines.push(<Polyline key={getPolylineId(segment.id, 2)} path={segment.path} options={mapConfig.hoverStyle.middle} />);
        lines.push(<Polyline key={getPolylineId(segment.id, 3)} path={segment.path} options={mapConfig.hoverStyle.outer} />);

        return lines;
      }
      if (segment.rings) {
        return [
          <Polygon
            key={getPolylineId(segment.id, 0)}
            paths={segment.rings}
            options={{...mapConfig.polygonHoverStyle.inner, strokeColor: color, fillColor: color, zIndex}}
          />,
          <Polygon
            key={getPolylineId(segment.id, 1)}
            paths={segment.rings}
            options={{...mapConfig.polygonHoverStyle.middle, strokePosition: google.maps.StrokePosition.OUTSIDE}}
          />,
          <Polygon
            key={getPolylineId(segment.id, 2)}
            paths={segment.rings}
            options={{...mapConfig.polygonHoverStyle.outer, strokePosition: google.maps.StrokePosition.OUTSIDE}}
          />
        ];
      }
      return [
        <Polyline key={getPolylineId(segment.id, 0)} path={[segment.center, segment.center]} options={innerOptions} />,
        <Polyline key={getPolylineId(segment.id, 1)} path={[segment.center, segment.center]} options={mapConfig.hoverStyle.middle} />,
        <Polyline key={getPolylineId(segment.id, 2)} path={[segment.center, segment.center]} options={mapConfig.hoverStyle.outer} />
      ];
    }
    if (segment.path) {
      return <Polyline key={getPolylineId(segment.id, 0)} path={segment.path} options={{ ...getInnerStyle(color), zIndex }} />;
    }
    if (segment.rings) {
      return (
        <Polygon
          key={getPolylineId(segment.id, 0)}
          paths={segment.rings}
          options={{ ...getInnerStyle(color), fillColor: color, zIndex }}
        />
      );
    }
    // If there is no path, nor rings, it's a point, render it on the map.
    return (
      <Polyline
        key={getPolylineId(segment.id, 0)}
        path={[segment.center, segment.center]}
        options={{...getInnerStyle(color), strokeWeight: 6, zIndex}}
      />
    );
  }, [color, getInnerStyle, getPolylineId, getSegmentArrowOptions, highlighted, selectedSegmentId]);

  if (markerVisible) {
    return segments.map(segment => {
      const highlight = isHighlighted(segment.id);
      return (
        <div key={getSegmentId(segment.id)}>
          <AdvancedMarker
            clickable={isClickable}
            position={segment.center}
            onMouseEnter={markerMouseEnters[segment.id]}
            onClick={markerClicks[segment.id]}
            title={markerTitle}
            zIndex={highlight ? 210 : 199}
            {...getMarkerAnchorStyle(getMarker(highlight))}
          >
            <MarkerIcon icon={getMarker(highlight)} />
          </AdvancedMarker>
          {showOffset &&
            <AdvancedMarker
              clickable={isClickable}
              position={segment.center}
              title={markerTitle}
              zIndex={highlight ? 211 : 200}
              {...getMarkerAnchorStyle(getOffsetMarker(highlight))}
            >
              <MarkerIcon icon={getOffsetMarker(highlight)} />
            </AdvancedMarker>
          }
          {renderSegment(segment, highlight)}
        </div>
      );
    });
  }

  return segments.map(segment => renderSegment(segment, isHighlighted(segment.id)));
};

EntityMapFigure.propTypes = {
  agencyTypeId: PropTypes.number,
  clustering: PropTypes.bool,
  color: PropTypes.string,
  highlighted: PropTypes.bool,
  icon: PropTypes.number,
  id: PropTypes.number,
  markerSource: PropTypes.string,
  markerVisible: PropTypes.bool,
  name: PropTypes.string,
  openConflicts: PropTypes.array,
  opportunities: PropTypes.string,
  ownEntity: PropTypes.bool,
  pendingConflicts: PropTypes.array,
  resolvedConflicts: PropTypes.array,
  segments: PropTypes.array,
  type: PropTypes.string
};

export default memo(EntityMapFigure);
